import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { ArrowUpDown } from 'lucide-react'
import Gravatar from 'react-gravatar'
import { formatCurrency } from '@/lib/utils'
import { GetOrgCreditAnalyticsData } from '@/services/inference.gen'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type AnalyticsOverviewTableProps = {
  data: GetOrgCreditAnalyticsData[]
}

export function AnalyticsOverviewTable({ data }: AnalyticsOverviewTableProps) {
  const [sortBy, setSortBy] = useState<string>('balance_summary')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const handleSort = (columnId: string) => {
    if (sortBy === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(columnId)
      setSortDirection('desc')
    }
  }

  const sortedData = [...data].sort((a, b) => {
    if (sortBy === 'balance_summary') {
      const valueA = a.balance_summary.total - a.balance_summary.spent_total
      const valueB = b.balance_summary.total - b.balance_summary.spent_total
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA
    }
    if (sortBy === 'left_time') {
      const valueA = a.left_time
      const valueB = b.left_time
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA
    }
    return 0
  })

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }

  if (!sortedData.length) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-muted-foreground">
          No overview data available for this time period
        </p>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className="w-full rounded-lg border shadow-sm overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-[45%] font-bold">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help">Users List</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      List of users in the organization with their profile
                      information
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead className="w-[27.5%]">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        variant="link"
                        className="px-0 text-foreground font-bold"
                        onClick={() => handleSort('left_time')}
                      >
                        Time Remaining
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Estimated time remaining based on current usage patterns
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead className="w-[27.5%]">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        variant="link"
                        className="px-0 text-foreground font-bold"
                        onClick={() => handleSort('balance_summary')}
                      >
                        Remaining Balance
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Current account balance minus total spent amount</p>
                  </TooltipContent>
                </Tooltip>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((item) => {
              const remainingBalance =
                item.balance_summary.total - item.balance_summary.spent_total
              const formattedBalance = formatCurrency(remainingBalance / 100)

              return (
                <TableRow key={item.user_id} className="hover:bg-muted/20">
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3 ml-3">
                      {item.profile_metadata?.avatar_preview_url ? (
                        <img
                          src={item.profile_metadata.avatar_preview_url}
                          alt={`${item.profile_metadata?.first_name || ''} ${item.profile_metadata?.last_name || ''}'s avatar`}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <Gravatar
                          email={item.email}
                          className="h-10 w-10 rounded-full"
                        />
                      )}

                      <div>
                        <div className="font-medium">
                          {item?.profile_metadata?.first_name || '-'}{' '}
                          {item?.profile_metadata?.last_name || '-'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {item.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    {item.left_time < 0 ? 'N/A' : formatTime(item.left_time)}
                  </TableCell>
                  <TableCell className="py-4 font-medium">
                    {formattedBalance}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  )
}
